import { SofItemEdit } from '../../../models/sof-receiving/SofReceivingLogItem.interface';

/**
 * Selects the item rows a Save / Mark-ready should PUT to
 * `/V1/sof-receiving/items`.
 *
 * Dirty-only isn't enough. The server stamps the `SOF #<sof>` notes prefix on
 * whatever rows it's handed, so a row that never reaches it never gets tagged —
 * and the two rows that most need the tag are exactly the clean ones: an item
 * pulled in a prior session (`alreadyPulled`), and an item whose state was
 * already correct so the picker never touched it. Both are being staged under
 * this SOF, so both must carry the SOF number in their notes.
 *
 * So the batch is the union of:
 * - every dirty row (checked, unchecked, or notes-edited this session), and
 * - every currently checked-out row, dirty or not.
 *
 * Unchecked *and* clean rows are deliberately excluded: this SOF isn't staging
 * them, and sending them would rewrite notes on receiving-log rows we have no
 * business touching. Rows that are both dirty and checked appear once.
 *
 * Un-checking is preserved (a dirty, now-unchecked row still goes) — it's the
 * only way a mistaken check gets undone. Already-pulled rows ride along with
 * their existing `checkedOut` value, so the caller re-sends `true` and only the
 * notes normalize; the server's TakenBy/DateOut writes are first-check-wins, so
 * the original picker's credit is untouched.
 *
 * Pure — returns a new array in input order; does not mutate.
 *
 * @param items - Every loaded item across the SOF's POs.
 * @returns The subset to send, deduped by `id`, in the order they appear.
 */
export function itemsToPersist(items: SofItemEdit[]): SofItemEdit[] {
  const seen = new Set<number>();
  return items.filter((item) => {
    if (!item.dirty && !item.checkedOut) {
      return false;
    }
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
}
